import { ASTRA_CHAT_TEST_IDS } from '@amzn/gravion-ui-contracts';
import React, { useCallback } from 'react';
import { AgentChatPanel } from '@amzn/gsce-shared-react-lib/components';
import {
  ASTRA_ACTIONS,
  ASTRA_PAGES,
  AstraI18nProvider,
  useAstra,
  useAstraPanelProps,
} from '../features/astra';
import {
  AstraPanelFrame,
  useAstraPanelSize,
  type AstraPanelSizeState,
} from './AstraPanelFrame';
import { V2Adapter } from './V2Adapter';

/** Root test id for the desktop panel's `AgentChatPanel`. */
export const ASTRA_DESKTOP_PANEL_TEST_ID = ASTRA_CHAT_TEST_IDS.desktopPanel;

/**
 * The Stencil-styled Astra panel for the desktop shell — the shared
 * `AgentChatPanel` inside a bottom-right resizable frame.
 *
 * `V2Adapter` supplies Stencil tokens and bridges the V1 user context to
 * `useCurrentUser`; `AstraI18nProvider` supplies the react-i18next instance
 * the panel translates through. The conversation itself comes from the
 * `AstraProvider` App.tsx already mounts.
 *
 * Header controls: new conversation, expand (maps to the frame's maximize
 * mode) and close. No minimize — see `AgentChatPanelProps` for why.
 */
export function AstraDesktopPanel(): React.JSX.Element | null {
  const { state } = useAstra();
  // Size state lives here, above the `isOpen` early return, so it stays
  // mounted while the panel is closed and the user's maximize/drag choice
  // survives close + reopen.
  const sizeState = useAstraPanelSize();
  if (!state.isOpen) return null;
  return (
    <V2Adapter>
      <AstraI18nProvider>
        <OpenAstraDesktopPanel sizeState={sizeState} />
      </AstraI18nProvider>
    </V2Adapter>
  );
}

interface OpenAstraDesktopPanelProps {
  readonly sizeState: AstraPanelSizeState;
}

function OpenAstraDesktopPanel({
  sizeState,
}: OpenAstraDesktopPanelProps): React.JSX.Element {
  const { togglePanel } = useAstra();
  const { isMaximized, toggleMaximize, frameProps } = sizeState;
  const { panelProps, trackAction } = useAstraPanelProps(ASTRA_PAGES.desktopPanel);

  const handleToggleExpand = useCallback((): void => {
    trackAction(ASTRA_ACTIONS.toggleExpand);
    toggleMaximize();
  }, [toggleMaximize, trackAction]);

  const handleClose = useCallback((): void => {
    trackAction(ASTRA_ACTIONS.close);
    togglePanel();
  }, [togglePanel, trackAction]);

  return (
    <AstraPanelFrame {...frameProps} ariaLabel={panelProps.agentName}>
      <AgentChatPanel
        {...panelProps}
        isExpanded={isMaximized}
        onToggleExpand={handleToggleExpand}
        onClose={handleClose}
        dataTestId={ASTRA_DESKTOP_PANEL_TEST_ID}
      />
    </AstraPanelFrame>
  );
}
